import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';

function Stat({ label, value, testid }) {
  return (
    <div className="border border-brand-line bg-brand-cream p-5">
      <p className="text-sm font-semibold text-brand-brown-soft">{label}</p>
      <p className="mt-1 font-serif text-4xl font-bold text-brand-brown" data-testid={testid}>{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api('/admin/dashboard').then(setData).catch((e) => setError(e.message));
  }, []);

  if (error) return <p role="alert" className="rounded-sm border border-red-700 bg-red-50 p-4 font-semibold text-red-800">{error}</p>;
  if (!data) return <p role="status" className="text-brand-brown-soft">Lade Dashboard …</p>;

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-3xl font-bold">Dashboard</h1>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Besucher heute" value={data.visitors_today} testid="stat-visitors-today" />
        <Stat label="Rezepte gesamt" value={data.recipes_total} testid="stat-recipes-total" />
        <Stat label="Veröffentlicht" value={data.published} testid="stat-published" />
        <Stat label="Entwürfe" value={data.drafts} testid="stat-drafts" />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <section className="border border-brand-line bg-brand-cream p-6" aria-labelledby="recent-heading">
          <h2 id="recent-heading" className="font-serif text-xl font-bold">Zuletzt bearbeitete Rezepte</h2>
          {data.recent_recipes.length === 0 ? (
            <p className="mt-3 text-sm text-brand-brown-soft">Noch keine Rezepte angelegt.</p>
          ) : (
            <ul className="mt-4 divide-y divide-brand-line">
              {data.recent_recipes.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 py-2.5">
                  <Link to={`/admin/rezepte/${r.id}`} className="font-semibold text-brand-olive underline-offset-4 hover:underline">{r.title}</Link>
                  <span className={`rounded-sm px-2 py-0.5 text-xs font-bold ${r.status === 'published' ? 'bg-brand-olive text-white' : 'bg-brand-beige text-brand-brown'}`}>
                    {r.status === 'published' ? 'Veröffentlicht' : r.status === 'draft' ? 'Entwurf' : 'Archiviert'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="border border-brand-line bg-brand-cream p-6" aria-labelledby="top-heading">
          <h2 id="top-heading" className="font-serif text-xl font-bold">Meistbesuchte Rezeptseiten (30 Tage)</h2>
          {data.top_recipes.length === 0 ? (
            <p className="mt-3 text-sm text-brand-brown-soft">Noch keine Aufrufe erfasst.</p>
          ) : (
            <ul className="mt-4 divide-y divide-brand-line">
              {data.top_recipes.map((t) => (
                <li key={t.path} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <span className="font-semibold">{t.path}</span>
                  <span className="text-brand-brown-soft">{t.views} Aufrufe</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="mt-6 border border-brand-line bg-brand-cream p-6" aria-labelledby="quick-heading">
        <h2 id="quick-heading" className="font-serif text-xl font-bold">Schnellzugriff</h2>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/admin/rezepte/neu" className="btn-primary" data-testid="quick-new-recipe">Neues Rezept</Link>
          <Link to="/admin/seiten/datenschutz" className="btn-secondary" data-testid="quick-datenschutz">Datenschutz bearbeiten</Link>
          <Link to="/admin/seiten/impressum" className="btn-secondary" data-testid="quick-impressum">Impressum bearbeiten</Link>
        </div>
      </section>

      <section className="mt-6 border border-brand-line bg-brand-cream p-6" aria-labelledby="changes-heading">
        <h2 id="changes-heading" className="font-serif text-xl font-bold">Letzte Änderungen</h2>
        {data.last_changes.length === 0 ? (
          <p className="mt-3 text-sm text-brand-brown-soft">Noch keine Aktivitäten.</p>
        ) : (
          <ul className="mt-4 divide-y divide-brand-line text-sm">
            {data.last_changes.map((c) => (
              <li key={c.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
                <span><strong>{c.action}</strong> · {c.object}</span>
                <span className="text-brand-brown-soft">{c.user} · {new Date(c.ts).toLocaleString('de-CH')}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
